import { uid } from '../../../../lib/uid';
import {
  DISCIPLINARY_ACTION_TYPE,
  DISCIPLINARY_STATUS,
  type DisciplinaryActionType,
  type DisciplinaryStatus,
} from '../personnelTypes';

/**
 * A plausible disciplinary record, for Autogenerate.
 *
 * **Every value is synthetic and none of it lands in `rsys_pii`.** The section is ordinary
 * local data and names no person, so this follows `sampleEducation` rather than
 * `samplePersonnel` — the values read as real subjects and real sanctions, because an
 * unreadable one would make the generated record worse at the one job it has.
 *
 * **The offence is deliberately mundane.** A generated record sits on somebody's 201 file in a
 * development database, and a fabricated allegation that reads as serious is a worse thing to
 * find there than a tidy one — so it is a late arrival and a lost ID card, never misconduct.
 */
export function sampleDisciplinary(): {
  type: DisciplinaryActionType;
  caseNumber: string;
  subject: string;
  description: string;
  incidentDate: string;
  dateFiled: string;
  actionTaken: string;
  status: DisciplinaryStatus;
  resolutionDate: string;
  remarks: string;
} {
  const tag = uid().slice(0, 6);
  const today = new Date();

  /*
    DATES BUILT FROM LOCAL PARTS, never through `toISOString()`. That is the UTC day, which for
    a reader west of UTC is the day before the one they would have typed — the instant path
    reaching a calendar date, which is the thing `formatDateOnly` exists to prevent.
  */
  const day = (offset: number) => {
    const d = new Date(today.getFullYear(), today.getMonth(), today.getDate() - offset);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };

  /*
    A CONCLUDED record, so the generated one exercises the resolution date rather than leaving
    the field the server refuses to accept without. The dates are ordered the way the validator
    requires — incident, then filing, then resolution — because a generator that produced a
    rejection would be the one thing Autogenerate exists to prevent.
  */
  return {
    type: DISCIPLINARY_ACTION_TYPE.WrittenWarning,
    caseNumber: `ADM-${today.getFullYear()}-${tag.toUpperCase()}`,
    subject: `Repeated late arrival (${tag})`,
    description:
      'Sample record generated for testing. The employee arrived after the start of shift on ' +
      'several occasions within one month; the matter was raised with the unit head.',
    incidentDate: day(45),
    dateFiled: day(38),
    actionTaken: 'Written warning issued and counselling conducted with the unit head.',
    status: DISCIPLINARY_STATUS.Resolved,
    resolutionDate: day(20),
    remarks: 'Sample data, not a real record.',
  };
}
